import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardFooter } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AppLayout } from '@/components/layout/app-layout';
import { EncabezadoDeGestion } from '@/features/cotiza/components/encabezado-de-gestion';
import { OpportunitiesFilters } from '../components/opportunities-filters';
import { OpportunitiesTable } from '../components/opportunities-table';
import { opportunitiesService } from '../services/opportunities.service';
import { OpportunitiesSyncControl } from '../components/opportunities-sync-control';
import { useOpportunitiesStore } from '../store/opportunities.store';
import {
  buildPipelineLabelMap,
  buildStageLabelMap,
  type HubspotDealListItem,
  type HubspotPipeline,
  type OpportunitiesListResponse,
} from '../types/opportunity.types';

/** Filtro cliente: coincide si la query se encuentra en nombre, empresa o ID. */
function applyClientSearch(deals: HubspotDealListItem[], q: string): HubspotDealListItem[] {
  if (!q.trim()) return deals;
  const lower = q.toLowerCase();
  return deals.filter(
    (d) =>
      d.name.toLowerCase().includes(lower) ||
      d.hubspotDealId.toLowerCase().includes(lower) ||
      (d.hubspotCompany?.name ?? '').toLowerCase().includes(lower)
  );
}

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export function OpportunitiesListPage() {
  const { filters, setFilters } = useOpportunitiesStore();
  const [data, setData] = useState<OpportunitiesListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pipelines, setPipelines] = useState<HubspotPipeline[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  // Sube cuando «Actualizar ahora» termina: vuelve a pedir la lista con los mismos filtros.
  const [reloadKey, setReloadKey] = useState(0);
  const handleSynced = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    setIsLoading(true);
    opportunitiesService
      .list(filters)
      .then(setData)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [filters, reloadKey]);

  useEffect(() => {
    opportunitiesService
      .pipelines()
      .then((r) => setPipelines(r.data))
      .catch(() => {});
  }, []);

  const stageLabels = useMemo(() => buildStageLabelMap(pipelines), [pipelines]);
  const pipelineLabels = useMemo(() => buildPipelineLabelMap(pipelines), [pipelines]);

  // Filtro cliente sobre los deals de la página actual (empresa, ID)
  const visibleDeals = useMemo(
    () => applyClientSearch(data?.data ?? [], clientSearch),
    [data, clientSearch]
  );

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 1;

  const firstItem = data ? (data.page - 1) * data.pageSize + 1 : 0;
  const lastItem = data ? Math.min(data.page * data.pageSize, data.total) : 0;

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-5 px-4 py-8 md:px-8">
        {/* El encabezado de Gestión (artefacto, 19-oct): el paso «Oportunidades». */}
        <EncabezadoDeGestion
          activa="oportunidades"
          titulo="Oportunidades"
          subtitulo={
            data
              ? `${data.total.toLocaleString('es-BO')} oportunidades, sincronizadas desde HubSpot CRM.`
              : 'Sincronizadas desde HubSpot CRM.'
          }
          acciones={<OpportunitiesSyncControl onSynced={handleSynced} />}
        />

        {/* Filtros */}
        <Card className="border rounded-lg overflow-hidden p-0">
          <OpportunitiesFilters
            clientSearch={clientSearch}
            onClientSearch={setClientSearch}
            stageLabels={stageLabels}
            pipelineLabels={pipelineLabels}
          />
        </Card>

        {/* Tabla */}
        <OpportunitiesTable
          deals={visibleDeals}
          loading={isLoading}
          stageLabels={stageLabels}
          pipelineLabels={pipelineLabels}
        />

        {/* Paginación */}
        {data && data.total > 0 && (
          <Card className="border rounded-lg">
            <CardFooter className="flex items-center justify-between gap-4 py-3">
              {/* Página tamaño */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Filas por página:</span>
                <Select
                  value={String(filters.pageSize)}
                  onValueChange={(v) => setFilters({ pageSize: Number(v), page: 1 })}
                >
                  <SelectTrigger className="h-8 w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Info de rango */}
              <span className="text-sm text-muted-foreground tabular-nums">
                Mostrando {firstItem}–{lastItem} de {data.total.toLocaleString('es-BO')}
              </span>

              {/* Botones navegación */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page === 1}
                  onClick={() => setFilters({ page: filters.page - 1 })}
                >
                  Anterior
                </Button>
                <span className="text-sm tabular-nums">
                  {data.page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page >= totalPages}
                  onClick={() => setFilters({ page: filters.page + 1 })}
                >
                  Siguiente
                </Button>
              </div>
            </CardFooter>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
